<template>
  <v-container fluid>
    <v-card class="d-flex justify-space-between backgroundWhite mb-3" flat>
      <v-card flat class="backgroundWhite">
        <v-skeleton-loader v-if="!current_period" type="chip" />
        <v-chip v-else class="text-caption" color="backgroundLight" text-color="primaryOutline" label>
          <v-icon small class="mr-2" color="primaryOutline">mdi-clock-outline</v-icon>
          <span>{{ current_period }}</span>
          <!--TODO: When period skipped, text field is broken, maybe add another flag?-->
        </v-chip>
      </v-card>

      <v-spacer></v-spacer>

      <v-card flat class="mr-3 backgroundWhite">
        <v-btn outlined text @click="toggleChartVisibility('Ambient_Temperature')">
          <v-icon left :color="is_ambient_temperature_visible ? 'vtblue' : 'titleLight'">
            {{ ambient_temp_icon }}
          </v-icon>
          Ambient Temperature
        </v-btn>
      </v-card>
      <v-card flat class="d-flex mr-3 backgroundWhite">
        <v-btn-toggle dense multiple tile>
          <v-btn
            v-for="phase in general_config.chart_phase_options"
            :key="phase.id"
            text
            :color="phase.color"
            @click="togglePhase(phase.id, phase.label)"
          >
            <v-icon left :color="show_phases[phase.id] ? phase.color : 'titleLight'">
              {{ show_phases[phase.id] ? "mdi-eye" : "mdi-eye-off" }}
            </v-icon>
            {{ phase.label }}
          </v-btn>
        </v-btn-toggle>
      </v-card>
      <v-card flat class="backgroundWhite">
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-btn @click="showSettingsMenu($event)" text outlined color="vtred" v-bind="attrs" v-on="on">
              <v-icon> mdi-settings </v-icon>
            </v-btn>
          </template>
          <span>Chart Settings</span>
        </v-tooltip>
      </v-card>

      <v-spacer></v-spacer>

      <v-card class="backgroundWhite" flat>
        <DatePickerVT @updatePeriod="updatePeriod" :is_loading="is_loading" />
      </v-card>
    </v-card>

    <div v-if="chart_data && events_data && powerdata_type && !is_loading">
      <LightningChartVT
        :chart_data="chart_data"
        :config="page_content.scavenger_charts"
        :events_config="page_content.event_types"
        :visible_charts="charts"
        :charts="charts"
        :types="types"
        :new_type_data="new_lc_data"
        :real_time_data="real_time_data"
        :is_loading="is_loading"
        :settings_menu_props="settings_menu_props"
        :toggle_phase="toggle_phase"
        :new_chart_data="toggle_chart"
        :visible_types="visible_types"
        :is_live_stream_on="is_live_stream_on"
        :events_data="events_data"
        @getTypeData="getTypeData"
        @setVisibleTypes="setVisibleTypes($event)"
        @resetChartOptions="resetChartOptions"
      />
    </div>
    <div v-else>
      <v-skeleton-loader
        v-for="(chart, index) in page_content.chart_config_lightning"
        :key="`loader-${index}`"
        type="image"
        class="py-3"
      />
    </div>
  </v-container>
</template>

<script>
import { axios_get } from "../services/vt_service";
import { h2, scv, weather } from "../services/powerdata";
import { content } from "../../config/scavenger.js";
import { content as h2_content } from "../../config/scavenger_h2.js";
import { content as general_content } from "../../config/general.js";
import { scavenger_grpc_services } from "../services/ScavengerServices";

import LightningChartVT from "../components/LightningChartVT.vue";
import DatePickerVT from "../components/DatePickerVT.vue";
import moment from "moment";

export default {
  name: "ScavengerAssetCharts",
  props: {
    device_id: String,
    as_of: String,
    start_date: String,
    end_date: String,
    device_data: Object,
    realtime_data: Array,
    has_location: Boolean,
  },
  components: {
    LightningChartVT,
    DatePickerVT,
  },
  data: function () {
    return {
      page_content: null,
      current_data: null,
      chart_data: null,
      charts: [],
      scv_charts: ["RMSVoltage", "ActivePower", "ReactivePower", "ApparentPower"],
      h2_charts: ["H2", "OilTemperature"],
      temperature_charts: ["AmbientTemperature"],
      types: ["Mean"],
      visible_types: ["Mean"],
      new_lc_data: null,
      is_loading: false,
      settings_menu_props: null,
      general_config: null,
      show_phases: [true, true, true],
      toggle_phase: null,
      real_time_data: null,
      is_live_stream_on: true, // Default to live stream, if false, new data point will not be added to the chart
      events_data: null,
      is_ambient_temperature_visible: false,
      ambient_temperature_data: null,
      toggle_chart: null,
      current_period_values: null,
    };
  },
  created() {
    this.page_content = this.powerdata_type == "h2scan" ? h2_content : content;
    this.general_config = general_content;
    if (this.device_data.TempSensor) this.scv_charts.push("Temperature");
  },
  async mounted() {
    this.chart_data = await this.getChartData();
    this.events_data = await this.getScavengerEvents();
  },
  methods: {
    async getChartData(parameters) {
      const type = parameters && parameters.types ? parameters.types[0] : null;

      const scavenger_chart_data = await this.getScavengerChartData(parameters);
      const scv_formatted_data = this.formatChartData(scavenger_chart_data, this.scv_charts, type);

      const scavenger_h2_chart_data = await this.getH2ChartData(parameters);
      const h2_formatted_data = this.formatChartData(scavenger_h2_chart_data, this.h2_charts, type);

      // Initially we are not showing the ambient temperature data, but it needs to be added to the chart and set the visibility to false
      // Later, we can show the ambient temperature data by toggling the button
      const temperature_data = await this.getAmbientTempChartData(parameters);
      const temperature_formatted_data = this.formatChartData(temperature_data, this.temperature_charts, type);

      return { ...scv_formatted_data, ...h2_formatted_data, ...temperature_formatted_data };
    },
    async getScavengerChartData(parameters) {
      // This API call is to get the initial data for the new lightning charts
      this.is_loading = this.is_live_stream_on ? false : true; // The reason for this is to show the loading skeleton only when the live stream is off

      const params = {
        start_date: null,
        end_date: null,
        aggregation: null,
        charts: this.scv_charts,
        types: this.types,
      };

      if (parameters) Object.assign(params, parameters);

      const response = await axios_get(scv.get_scv_chart_data, { id: this.device_id }, params);
      if (!parameters) this.charts.push(...this.scv_charts); // To prevent adding the same chart multiple times
      this.is_loading = false;
      return response;
    },
    async getH2ChartData(parameters) {
      if (this.powerdata_type != "h2scan") return [];

      this.is_loading = this.is_live_stream_on ? false : true;

      const params = {
        start_date: null,
        end_date: null,
        aggregation: null,
        charts: this.h2_charts,
        types: this.types,
      };

      if (parameters) Object.assign(params, parameters);

      const response = await axios_get(h2.get_h2_chart_data, { id: this.device_id }, params);
      if (!parameters) this.charts.push(...this.h2_charts); // To prevent adding the same chart multiple times
      this.is_loading = false;
      return response;
    },
    async getAmbientTempChartData(parameters) {
      if (!parameters) this.charts.push(...this.temperature_charts);
      if (!this.is_ambient_temperature_visible) return []; // If the ambient temperature is not visible, no need to make the API call in the initial load

      const params = {
        lat: this.device_data.CurrentLat,
        lng: this.device_data.CurrentLng,
        start_date: null,
        end_date: null,
        aggregation: null,
      };

      if (parameters) Object.assign(params, parameters);

      const response = await axios_get(weather.fetch_ambient_temp_data, {}, params);

      return response;
    },
    async getTypeData(type) {
      this.is_loading = true;
      // We need to call getScavengerChartData API with only with the received type
      const parameters = {
        start_date: null,
        end_date: null,
        types: [type],
      };

      this.new_lc_data = await this.getChartData(parameters);
      this.types.push(type);
      this.is_loading = false;
    },
    async getScavengerEvents(params) {
      const parameters = params ? params : { duration: 1 };
      const response = await axios_get(scv.get_scavenger_events, { id: this.device_id }, parameters);
      const formatted_events = this.formatEventsData(response);
      return formatted_events;
    },
    formatEventsData(raw_events) {
      let temp = {};
      raw_events.map((e) => {
        if (e.event.endTimestamp && e.event.startTimestamp) {
          if (!temp[e.event_id + "_" + e.subid])
            temp[e.event_id + "_" + e.subid] = {
              deviceId: e.deviceId,
              eventType: e.eventType,
              type: this.page_content.events[e.eventType],
              messageType: e.messageType,
              id: `${e.event_id ? e.event_id : 0}_${e.subid}`,
              event_id: e.event_id,
              subid: e.subid,
              timestampISO: e.timestampISO,
              endTimestamp: e.event.endTimestamp,
              startTimestamp: e.event.startTimestamp,
              duration:
                e.event.endTimestamp - e.event.startTimestamp
                  ? (e.event.endTimestamp - e.event.startTimestamp) / 3600000
                  : 0,
              values: [],
            };
          temp[e.event_id + "_" + e.subid].values.push(e.event);
        }
      });
      let events = Object.keys(temp).map((e) => temp[e]);
      return raw_events ? events : null;
    },
    formatChartData(data, format_charts = this.charts, type) {
      // In the initial call, the types will be pre-defined, so we can directly format the data
      // However, later we can get new type data from the API and format it accordingly
      // So, no need to format the existing data again

      const types = type ? [type] : this.types;
      const formatted_data = {};

      format_charts.forEach((chart) => {
        const charts = [];
        const chart_key = chart.split("_").pop();
        const chart_config = this.page_content.scavenger_charts[chart_key];
        types.forEach((type) => {
          chart_config.values.forEach((value) => {
            const value_id = value ? `${value}_${chart_config.id}_${type}` : `${chart_config.id}`;
            const chart_data = {
              id: value_id,
              config_id: chart,
              type: type,
              value: value,
              label: `${value} ${chart_config.label} ${type}`,
              values: {
                xValues: data.map((e) => moment(e.Timestamp).valueOf()),
                yValues: data.map((e) => this.getDataPointValue(e, value_id, chart_config)),
              },
            };
            charts.push(chart_data);
          });
        });
        formatted_data[chart] = charts;
      });
      return formatted_data;
    },
    showSettingsMenu(e) {
      this.settings_menu_props = { state: true, x: e.clientX, y: e.clientY };
    },
    resetChartOptions() {
      this.visible_types = ["Mean"];
      this.types = ["Mean"];
    },
    async updatePeriod(e) {
      const parameters = {
        start_date: e.start_date,
        end_date: e.end_date,
        aggregation: e.aggregation,
        types: this.types,
      };
      this.current_period_values = e;
      this.is_live_stream_on = e.liveStream;
      this.events_data = await this.getScavengerEvents(parameters);
      this.chart_data = await this.getChartData(parameters);
    },
    getDataPointValue(value, value_id, config) {
      if (config && config.suffix && config.suffix == "F") return this.$options.filters.toFahrenheit(value[value_id]);
      return parseFloat(value[value_id] ? value[value_id] : null);
    },
    async toggleChartVisibility(chart) {
      // This function is to toggle the visibility of the any chart if needed in the future for the other charts
      // For now, it is only used for the Ambient Temperature chart
      this.is_ambient_temperature_visible = !this.is_ambient_temperature_visible;
      let data = null;
      let show = this.charts;
      if (!this.ambient_temperature_data) {
        const response = await this.getAmbientTempChartData(true);
        this.ambient_temperature_data = this.formatChartData(response, this.temperature_charts);
        data = this.ambient_temperature_data;
      }
      if (!data && !this.is_ambient_temperature_visible) show = show.filter((c) => c !== "AmbientTemperature");
      this.toggle_chart = show.reduce((acc, c) => {
        acc[c] = {
          data: data ? data[c] : null,
          show: true,
        };
        return acc;
      }, {});
    },
    togglePhase(id, label) {
      this.show_phases[id] = !this.show_phases[id];
      this.toggle_phase = { value: label, show: this.show_phases[id] };
    },
    setVisibleTypes(event) {
      if (event.show) {
        this.visible_types.push(event.type);
      } else {
        this.visible_types = this.visible_types.filter((type) => type !== event.type);
      }
    },
  },
  computed: {
    current_period() {
      if (this.is_live_stream_on) {
        return `Last 24 Hrs: ${new Date(parseInt(this.start_date)).toLocaleString("en-US", {
          hour12: false,
        })}`;
      }
      return `${this.current_period_values.text}: ${moment(this.current_period_values.end_date).format("D MMM")} - ${moment(
        this.current_period_values.start_date
      ).format("D MMM YYYY")}`;
    },
    powerdata_type: function () {
      return this.$store.state.tenant_config ? this.$store.state.tenant_config.powerdata_type : null;
    },
    ambient_temp_icon() {
      return this.is_ambient_temperature_visible ? "mdi-eye" : "mdi-eye-off";
    },
  },
  watch: {
    as_of: async function (newVal, oldVal) {
      if (newVal != oldVal) {
        this.getBoxChartData();
        await this.getScavengerChartData();
        this.current_data = this.formatting_chart_data();
        if (this.$store.getters.getPowerDataType == "h2scan") {
          this.getH2RawMessages();
        }
      }
    },
    realtime_data: function (newVal, oldVal) {
      if (newVal && newVal != oldVal) {
        this.real_time_data = this.formatChartData(newVal);
      }
    },
  },
};
</script>
